import { Divider, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FunctionComponent, MouseEvent, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { AverageCycleCountWidgetConfiguration } from './AverageCycleCountWidgetConfiguration';
import { useDashboardContext } from '@/modules/dashboards/contexts/DashboardContextProvider';
import { LocationSelect } from '@/modules/locations/components/LocationSelect';
import LabelValuesSelect from '@/modules/labels/components/LabelValuesSelect';
import AssetTypeSelect from '@/modules/asset-types/components/AssetTypeSelect';
import { useTranslation } from '@/lib';

interface AverageCycleCountWidgetConfigurationFormProps {
  configuration: AverageCycleCountWidgetConfiguration;
  id: string;
}

const AverageCycleCountWidgetConfigurationForm: FunctionComponent<AverageCycleCountWidgetConfigurationFormProps> = ({
  configuration,
  id,
}) => {
  const { updateWidgetConfiguration } = useDashboardContext();
  const { t } = useTranslation();

  // The cycle starting point is either a location or one or more label values; the toggle makes it clear only one can be chosen.
  const [cycleStartMode, setCycleStartMode] = useState<'location' | 'label'>(
    (configuration.cycleStartLabelValueIds?.length ?? 0) > 0 ? 'label' : 'location',
  );

  function handleCycleStartModeChange(_event: MouseEvent<HTMLElement>, mode: 'location' | 'label' | null) {
    if (!mode) return; // ignore de-selecting the active option
    setCycleStartMode(mode);
    // Clear the other value so only one of location/label is ever set.
    updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(
      id,
      mode === 'location' ? { cycleStartLabelValueIds: [] } : { cycleStartLocationId: undefined },
    );
  }

  // The current location filter is also either a location or one or more label values (one or the other).
  const [currentMode, setCurrentMode] = useState<'location' | 'label'>(
    (configuration.currentLabelValueIds?.length ?? 0) > 0 ? 'label' : 'location',
  );

  function handleCurrentModeChange(_event: MouseEvent<HTMLElement>, mode: 'location' | 'label' | null) {
    if (!mode) return; // ignore de-selecting the active option
    setCurrentMode(mode);
    // Clear the other value so only one of location/label is ever set.
    updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(
      id,
      mode === 'location' ? { currentLabelValueIds: [] } : { currentLocationId: undefined },
    );
  }

  return (
    <>
      <TextField
        label={t('name')}
        value={configuration.title}
        onChange={(e) => {
          updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
            title: e.currentTarget.value,
          });
        }}
      />

      {/* Required: Asset type */}
      <AssetTypeSelect
        selectedAssetTypeId={configuration.assetTypeId}
        onChange={(selectedAssetType) => {
          updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
            assetTypeId: selectedAssetType?.id,
          });
        }}
        label={t('asset_type')}
      />

      {/* Required: Cycle starting point — either a location OR a label */}
      <div className="flex flex-col gap-y-2">
        <div className="text-sm font-medium dark:text-gray-300">{t('dashboard.averageCycleCountWidgetConfiguration.cycleStart')}</div>
        <ToggleButtonGroup exclusive fullWidth size="small" value={cycleStartMode} onChange={handleCycleStartModeChange}>
          <ToggleButton value="location">{t('dashboard.averageCycleCountWidgetConfiguration.locationOption')}</ToggleButton>
          <ToggleButton value="label">{t('dashboard.averageCycleCountWidgetConfiguration.labelOption')}</ToggleButton>
        </ToggleButtonGroup>

        {cycleStartMode === 'location' ? (
          <LocationSelect
            selectedLocationId={configuration.cycleStartLocationId}
            onSelected={(selectedLocation) => {
              updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
                cycleStartLocationId: selectedLocation?.dto.id,
              });
            }}
            label={t('dashboard.averageCycleCountWidgetConfiguration.locationOption')}
          />
        ) : (
          <LabelValuesSelect
            selectedValues={configuration.cycleStartLabelValueIds ?? []}
            onChange={(values) => {
              updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
                cycleStartLabelValueIds: values,
              });
            }}
          />
        )}
      </div>

      <div className="mt-2">
        <div className="text-md dark:text-gray-300">{t('dashboard.averageCycleCountWidgetConfiguration.filters')}</div>
        <Divider className="mt-1!" />
      </div>

      {/* Optional: Current location — either a location OR one or more label values */}
      <div className="flex flex-col gap-y-2">
        <div className="text-sm font-medium dark:text-gray-300">{t('dashboard.averageCycleCountWidgetConfiguration.currentLocation')}</div>
        <ToggleButtonGroup exclusive fullWidth size="small" value={currentMode} onChange={handleCurrentModeChange}>
          <ToggleButton value="location">{t('dashboard.averageCycleCountWidgetConfiguration.locationOption')}</ToggleButton>
          <ToggleButton value="label">{t('dashboard.averageCycleCountWidgetConfiguration.labelOption')}</ToggleButton>
        </ToggleButtonGroup>

        {currentMode === 'location' ? (
          <LocationSelect
            selectedLocationId={configuration.currentLocationId}
            onSelected={(selectedLocation) => {
              updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
                currentLocationId: selectedLocation?.dto.id,
              });
            }}
            label={t('dashboard.averageCycleCountWidgetConfiguration.locationOption')}
          />
        ) : (
          <LabelValuesSelect
            selectedValues={configuration.currentLabelValueIds ?? []}
            onChange={(values) => {
              updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
                currentLabelValueIds: values,
              });
            }}
          />
        )}
      </div>

      {/* Optional: Start and end date */}
      <DatePicker
        className="w-full"
        label={t('dashboard.averageCycleCountWidgetConfiguration.startDate')}
        format="DD-MM-YYYY"
        value={configuration.startDateUTC ? dayjs(configuration.startDateUTC) : null}
        slotProps={{ field: { clearable: true } }}
        onChange={(value: Dayjs | null) => {
          updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
            startDateUTC: value ? value.toISOString() : undefined,
          });
        }}
      />

      <DatePicker
        className="w-full"
        label={t('dashboard.averageCycleCountWidgetConfiguration.endDate')}
        format="DD-MM-YYYY"
        value={configuration.endDateUTC ? dayjs(configuration.endDateUTC) : null}
        slotProps={{ field: { clearable: true } }}
        onChange={(value: Dayjs | null) => {
          updateWidgetConfiguration<AverageCycleCountWidgetConfiguration>(id, {
            endDateUTC: value ? value.toISOString() : undefined,
          });
        }}
      />
    </>
  );
};

export default AverageCycleCountWidgetConfigurationForm;
